<template>
  <v-row>
    <v-col>
      <!-- @after-leave, not the v-model setter, is where the store gets blanked.
           clearModalDialog() hides the dialog and THEN clears title/captions/
           width in the same tick, so the content re-rendered to its defaults
           while the leave transition was still running: the OK button visibly
           snapped from "SET READY FOR CONTRACT" @270px back to "OK" @150px for
           the ~170ms of the fade. Deferring the clear to afterLeave keeps the
           dialog reading correctly until it is actually gone. Safe because
           setShowModalDialog() clears the store on every OPEN ("belt and
           suspenders"), so nothing depends on the close-time clear having run. -->
      <v-dialog
        v-model="propModel"
        :max-width="maxWidth"
        :overlay-opacity="overlayOpacity"
        persistent
        @keydown.esc="cancel"
        @after-leave="clearStoreValues"
      >
        <ModalDialogForm
          :title="title"
          :subtitle="subtitle"
          :message="message"
          :dialog-style="dialogStyle"
          :do-hide-cancel-button="doHideCancelButton"
          :ok-should-act-like-cancel="okShouldActLikeCancel"
          :ok-button-caption-verb-override="okButtonCaptionVerbOverride"
          :cancel-button-caption-verb-override="cancelButtonCaptionVerbOverride"
          :do-skip-verification="doSkipVerification"
          :ok-button-width="okButtonWidth"
          :max-width="maxWidth"
          :is-showing="propModel"
          :is-processing="isProcessing"
          @ok="didClickOkAndVerify"
          @cancel="cancel"
        />
      </v-dialog>
    </v-col>
  </v-row>
</template>

<script>
import { isDarkTheme } from '~/cynqueModules/nuxtAccessors'
import { useModalDialogStore } from '~/stores/modalDialog'
/* eslint-disable no-console */

export default {
  props: {
    showModalDialog: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    mn: '[components/defaultLayout/ModalDialog.vue]',
  }),
  computed: {
    propModel: {
      get() {
        return this.showModalDialog
      },
      set(value) {
        const fn = `${this.mn} set propModel |`
        console.debug(`${fn} value:`, value)
        if (value === false) {
          console.debug(`${fn} value is 'false'. Hiding the dialog.`)
          // Hide only. The store is blanked in @after-leave, once the fade ends.
          useModalDialogStore().setShowModalDialog(false)
        }
      },
    },
    maxWidth() {
      return 580
    },
    overlayOpacity() {
      isDarkTheme() ? 0.8 : 0.5
    },
    title() {
      return useModalDialogStore().title
    },
    subtitle() {
      return useModalDialogStore().subtitle
    },
    message() {
      return useModalDialogStore().message
    },
    dialogStyle() {
      return useModalDialogStore().dialogStyle
    },
    doHideCancelButton() {
      return useModalDialogStore().doHideCancelButton
    },
    okShouldActLikeCancel() {
      return useModalDialogStore().okShouldActLikeCancel
    },
    okButtonCaptionVerbOverride() {
      return useModalDialogStore().okButtonCaptionVerbOverride
    },
    okButtonWidth() {
      return useModalDialogStore().okButtonWidth
    },
    doSkipVerification() {
      return useModalDialogStore().doSkipVerification
    },
    cancelButtonCaptionVerbOverride() {
      return useModalDialogStore().cancelButtonCaptionVerbOverride
    },
    isProcessing() {
      return useModalDialogStore().isProcessing
    },
  },
  watch: {
    showModalDialog(value, oldValue) {
      console.debug(
        `${this.mn} watch showModalDialog | changed from '${oldValue}' to '${value}'`
      )
    },
  },
  methods: {
    // The OK dispatch.
    //
    // This used to be nothing but `setDidClickOk(true)`, with the actual call
    // sitting in a `didClickOk` watcher duplicated VERBATIM across twelve row-menu
    // components. Whichever of them happened to be mounted and flushed first ran
    // the callback — so the console named an unrelated menu as the dispatcher —
    // and the two menus that register a callback without carrying the watcher
    // (Order/OpportunityMasterServiceMenu) depended entirely on a stranger being
    // on screen. The dialog was given the callback; the dialog calls it. #124.
    async didClickOkAndVerify() {
      const fn = `${this.mn} didClickOkAndVerify |`
      const store = useModalDialogStore()
      const onOkCallback = store.onOkCallback
      const onOkCallbackData = store.onOkCallbackData
      // Single-fire: drop the callback BEFORE running it. OK is only disabled
      // once an action sets isProcessing and not every callback does, so a second
      // click could otherwise re-enter the action.
      await store.clearOnOkCallback()
      // The flag itself is kept: it is the trace people grep for, and clearing it
      // after the call preserves the old lifecycle exactly.
      await store.setDidClickOk(true)
      if (typeof onOkCallback === 'function') {
        console.debug(`${fn} calling onOkCallback with:`, onOkCallbackData)
        // NOT awaited, as the watchers never were: `$emit` dispatches through
        // Vue's callWithAsyncErrorHandling, which owns a rejected handler.
        onOkCallback(onOkCallbackData)
      } else {
        console.debug(`${fn} no onOkCallback registered.`)
      }
      await store.clearDidClickOk()
    },
    cancel() {
      this.close()
    },
    close() {
      this.propModel = false
    },
    async clearStoreValues() {
      const fn = `${this.mn} clearStoreValues |`
      console.debug(`${fn} leave transition finished; calling clearModalDialog`)
      await useModalDialogStore().clearModalDialog()
    },
  },
}
</script>

<style scoped>
:deep(.v-dialog) {
  overflow-x: hidden;
}
</style>
